/* eslint-disable import/prefer-default-export */
import { getOsgBrowserCompassByKey, getOsgCurrentRegionCode } from '@osgfe/web-compass';
import { MCity } from '@sailor/i18n-vue';

export const detectDeviceType = () => (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop');

/**
 * EU 降级兜底数据（接口请求失败时使用）
 */
const EU_FALLBACK_REGION_PACKS = [
    {
        code: 'SA',
        name: 'Saudi Arabia',
        names: [
            { lang: 'en', name: 'Saudi Arabia' },
            { lang: 'zh', name: '沙特阿拉伯' },
            { lang: 'ar', name: 'المملكة العربية السعودية' },
        ],
        langs: [
            { code: 'en', name: 'English' },
            { code: 'ar', name: 'العربية' },
            { code: 'zh', name: '中文（简体）' },
        ],
    },
    {
        code: 'KW',
        name: 'Kuwait',
        defaultName: {
            lang: 'ar',
            name: 'الكويت',
        },
        names: [
            { lang: 'en', name: 'Kuwait' },
            { lang: 'zh', name: '科威特' },
            { lang: 'zh-HK', name: '科威特' },
            { lang: 'ar', name: 'الكويت' },
            { lang: 'ur', name: 'Kuwait' },
            { lang: 'pt-BR', name: 'Kuwait' },
        ],
        langs: [
            { code: 'ar', name: 'العربية' },
            { code: 'en', name: 'English' },
            { code: 'zh', name: '中文（简体）' },
        ],
    },
];

/**
 * 获取 business 域名（从罗盘获取，支持泳道）
 * 罗盘 key: businessKeemartHost，获取标准域名（如 business.osg.test.sankuai.com）
 * 若当前页面 hostname 含 -sl-（泳道环境），则提取泳道前缀拼到域名前：
 *   例：58739-gscfp-sl-consumer.osg.test.sankuai.com
 *    → https://58739-gscfp-sl-business.osg.test.sankuai.com
 */
function getBusinessBaseUrl() {
    const baseHost = getOsgBrowserCompassByKey({
        key: 'businessKeemartHost',
        env: process.env.NODE_ENV === 'production' ? 'production' : 'test',
        region: getOsgCurrentRegionCode(),
    });
    const { hostname } = window.location;
    const slMatch = hostname.match(/^(.+)-sl-/);
    if (slMatch) {
        return `https://${slMatch[1]}-sl-${baseHost}`;
    }
    return `https://${baseHost}`;
}

/**
 * 将接口响应 data 转换为 regionPacks 数组
 * 接口已返回 regionName 多语言名称，直接动态解析，无需静态映射表
 */
function transformApiDataToRegionPacks(data) {
    const { supportRegions, supportLocales } = data;

    // 构建 locale code -> localizedDisplayName 索引
    const localeDisplayMap = {};
    (supportLocales || []).forEach(({ locale, localizedDisplayName }) => {
        localeDisplayMap[locale] = localizedDisplayName;
    });

    return Object.entries(supportRegions).map(([code, regionData]) => {
        const { supportLocales: regionLocales, regionName } = regionData;

        // 从接口 regionName 构建 names 数组：{ ar: "...", en: "...", zh: "..." } -> [{ lang, name }]
        const names = Object.entries(regionName || {}).map(([lang, name]) => ({ lang, name }));

        // 默认 name 优先取英文，否则取第一个
        const name = (regionName && regionName.en) || (names[0] && names[0].name) || code;

        return {
            code,
            name,
            names,
            langs: (regionLocales || []).map((localeCode) => ({
                code: localeCode,
                name: localeDisplayMap[localeCode] || localeCode,
            })),
        };
    });
}

/**
 * 根据 region code 获取首都城市 ID
 * 直接使用 @sailor/i18n-vue 的 MCity SDK，该 SDK 内部维护了各 region 的城市数据
 * @param {string} regionCode - region code，如 'SA'、'KW'
 * @returns {string} 首都城市 ID，找不到时返回空字符串
 */
export const getCapitalCityIdByRegion = (regionCode) => {
    if (!regionCode) return '';
    return MCity.getCapitalCityIdByRegion(regionCode) || '';
};

/**
 * 获取 RegionPacks 数据
 * - PUBLISH_IDC === 'EU'：通过接口动态获取，失败时降级到兜底数据
 * - 其他（HK 等）：原逻辑，非生产环境追加 HK，始终包含 SA
 */
export const getRegionPacks = async () => {
    const publishIdc = process_env_publishIdc;
    if (publishIdc === 'EU') {
        try {
            const baseUrl = getBusinessBaseUrl();
            const res = await fetch(`${baseUrl}/api/i18n/keemart/region-configs`);
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const json = await res.json();
            if (json.code === 0 && json.data) {
                return transformApiDataToRegionPacks(json.data);
            }
            throw new Error(`API code: ${json.code}`);
        } catch (e) {
            console.error('Failed to fetch region configs:', e);
            // 异常上报
            if (window.Owl) {
                window.Owl.addError(
                    {
                        name: 'region_config_fetch_error',
                        msg: e && e.message,
                    },
                    {
                        level: 'error',
                        tags: { url: `${getBusinessBaseUrl()}/api/i18n/keemart/region-configs` },
                    },
                );
            }
            // 降级到兜底数据，确保页面正常使用
            return EU_FALLBACK_REGION_PACKS;
        }
    }

    // PUBLISH_IDC === 'HK' 或其他：原逻辑
    const isProd = process.env.NODE_ENV === 'production';
    return [
        ...(isProd ? [] : [{
            code: 'HK',
            name: 'Hong Kong',
            names: [
                { lang: 'en', name: 'Hong Kong' },
                { lang: 'zh', name: '香港' },
            ],
            langs: [
                { code: 'en', name: 'English' },
                { code: 'zh', name: '中文（简体）' },
            ],
        }]),
        {
            code: 'SA',
            name: 'Saudi Arabia',
            names: [
                { lang: 'en', name: 'Saudi Arabia' },
                { lang: 'zh', name: '沙特阿拉伯' },
                { lang: 'ar', name: 'المملكة العربية السعودية' },
            ],
            langs: [
                { code: 'en', name: 'English' },
                { code: 'ar', name: 'العربية' },
                { code: 'zh', name: '中文（简体）' },
            ],
        },
    ];
};
